import React, { useEffect, useRef, useState } from 'react';
import { db } from '../Firebase';
import { doc, getDoc } from 'firebase/firestore';
import CountUp from 'react-countup';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FiTarget, FiEye, FiUsers, FiAward,
  FiCheckCircle, FiHeart, FiActivity, FiShield,
  FiTrendingUp, FiSmile
} from 'react-icons/fi';
import './About.css';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const pageRef = useRef(null);

  const [stats, setStats] = useState({
    param1Value: 'NABH',
    param1Label: 'Accredited Standards',
    yearsOfService: 15,
    happyPatients: 50,
    param4Value: '24/7',
    param4Label: 'Emergency Support'
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const docRef = doc(db, 'settings', 'aboutStats');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setStats(docSnap.data());
        }
      } catch (err) {
        console.error("Error fetching about stats: ", err);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Story section animation
      gsap.fromTo(
        '.story-image-wrapper',
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.about-story-section',
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );

      gsap.fromTo(
        '.story-content',
        { opacity: 0, x: 50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.about-story-section',
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );

      // Philosophy cards animation
      gsap.fromTo(
        '.philosophy-card',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.philosophy-container',
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );

      gsap.fromTo(
        '.ph-image-wrapper',
        { opacity: 0, scale: 0.95 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.philosophy-container',
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );

      // Values animation
      gsap.fromTo(
        '.value-card-premium',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.values-grid',
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );

      // Goals animation
      gsap.fromTo(
        '.goal-card',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.12,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.goals-grid',
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );

      gsap.fromTo(
        '.goals-visual-wrapper',
        { opacity: 0, x: 50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.about-goals-section',
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="about-page-wrapper" ref={pageRef}>
      {/* Header Section */}
      <header
        className="about-header"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10, 36, 34, 0.52) 0%, rgba(10, 36, 34, 0.62) 100%), url(${process.env.PUBLIC_URL}/assets/images/about.png)`,
        }}
      >
        <div className="about-header-overlay" />
        <div className="about-header-content">
          <span className="about-subtitle-tag">Swarup Multi-Speciality Hospital</span>
          <h1>About Our Hospital</h1>
          <p>
            Providing compassionate, patient-focused healthcare through experienced specialists, modern medical facilities and personalized treatment for individuals and families in Kolhapur.
          </p>
        </div>
      </header>

      {/* Story Section */}
      <section className="about-story-section">
        <div className="story-container">
          <div className="story-image-wrapper">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/image2.jpeg`}
              alt="Swarup Hospital Facility"
              className="story-image"
            />
            <div className="story-experience-badge">
              <span className="exp-number">40+</span>
              <span className="exp-text">YEARS OF TRUSTED< br />HEALTHCARE</span>
            </div>
          </div>
          <div className="story-content">
            <span className="section-tag">WHO WE ARE</span>
            <h2 className="section-title">Committed to Caring for Our Community</h2>
            <p className="story-lead">
              Swarup Hospital, Kolhapur is a multi-speciality healthcare centre dedicated to providing safe, ethical and patient-focused medical care through experienced doctors, modern facilities and personalized treatment.
            </p>
            <p className="story-body">
              Our team works together to deliver quality healthcare across multiple specialties while creating a comfortable and supportive environment for every patient and family.
            </p>
            <div className="story-features-grid">
              <div className="story-feature">
                <FiCheckCircle className="feature-icon" />
                <span>Experienced Multi-Speciality Medical Team</span>
              </div>
              <div className="story-feature">
                <FiCheckCircle className="feature-icon" />
                <span>24×7 Emergency Medical Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip (Numerical parameters) */}
      <section className="stats-strip">
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-value">{stats.param1Value || 'NABH'}</div>
            <p>{stats.param1Label || 'Accredited Standards'}</p>
          </div>
          <div className="stat-item">
            <div className="stat-value">
              <CountUp end={stats.yearsOfService} suffix="+" duration={2} enableScrollSpy scrollSpyOnce />
            </div>
            <p>Years of Service</p>
          </div>
          <div className="stat-item">
            <div className="stat-value">
              <CountUp end={stats.happyPatients} suffix="k+" duration={2.5} enableScrollSpy scrollSpyOnce />
            </div>
            <p>Happy Patients</p>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.param4Value || '24/7'}</div>
            <p>{stats.param4Label || 'Emergency Support'}</p>
          </div>
        </div>
      </section>

      {/* Philosophy (Mission & Vision) */}
      <section className="about-philosophy-section">
        <div className="philosophy-container">
          <div className="philosophy-text">
            <span className="section-tag">Our Philosophy</span>
            <h2 className="section-title">Purpose & Intent</h2>
            <p className="section-subtitle">
              Committed to providing quality healthcare with compassion, responsibility and continuous care for every patient.
            </p>

            <div className="philosophy-cards-stack">
              <div className="philosophy-card mission-card">
                <div className="ph-icon-box">
                  <FiTarget className="ph-icon" />
                </div>
                <div className="ph-content">
                  <h3>Our Mission</h3>
                  <p>
                    To Provide Exceptional Healthcare Services In A Safe and Trustful Environment Through The Expertise, Commitment And Compassion of Our Family of Care Givers.
                  </p>
                </div>
              </div>

              <div className="philosophy-card vision-card">
                <div className="ph-icon-box">
                  <FiEye className="ph-icon" />
                </div>
                <div className="ph-content">
                  <h3>Our Vision</h3>
                  <p>
                    Safe, Reliable, High Quality Care for Every Patient Every Day
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="philosophy-image-side">
            <div className="ph-image-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/img5.jpeg`}
                alt="Our dedicated team of care givers"
                className="ph-image"
              />
              <div className="ph-glass-card">
                <h4>Compassionate Care</h4>
                <p>Dedicated to supporting every patient with respect, empathy and personalized medical care throughout their healthcare journey.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="about-values-section">
        <div className="values-section-content">
          <div className="section-header">
            <span className="section-tag">How We Work</span>
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">
              These principles guide our decisions and define our culture, ensuring we deliver care of the highest integrity.
            </p>
          </div>

          <div className="values-grid">
            <div className="value-card-premium">
              <div className="val-icon-container">
                <FiUsers className="val-icon" />
              </div>
              <h4>Teamwork</h4>
              <p>Collaborating seamlessly across specialties to deliver integrated and comprehensive care for our patients.</p>
            </div>

            <div className="value-card-premium">
              <div className="val-icon-container">
                <FiShield className="val-icon" />
              </div>
              <h4>Professionalism</h4>
              <p>Maintaining the highest clinical and ethical standards, demonstrating excellence in every interaction.</p>
            </div>

            <div className="value-card-premium">
              <div className="val-icon-container">
                <FiAward className="val-icon" />
              </div>
              <h4>Pride on What We Do</h4>
              <p>Taking ultimate ownership and pride in the quality of care and support we provide each day.</p>
            </div>

            <div className="value-card-premium">
              <div className="val-icon-container">
                <FiHeart className="val-icon" />
              </div>
              <h4>Respect to Patient, Their Relatives</h4>
              <p>Treating every patient and their family members with dignity, empathy, and absolute confidentiality.</p>
            </div>

            <div className="value-card-premium">
              <div className="val-icon-container">
                <FiSmile className="val-icon" />
              </div>
              <h4>Respect to Our Co-workers</h4>
              <p>Fostering a supportive environment based on mutual respect, kindness, and collaboration among our peers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Goals Section */}
      <section className="about-goals-section">
        <div className="goals-container">
          <div className="goals-content-side">
            <span className="section-tag">Our Milestones</span>
            <h2 className="section-title">Our Commitment to Better Healthcare</h2>
            <p className="section-subtitle">
              Focused on improving patient care through experienced specialists, modern medical technology and continuous quality enhancement.
            </p>

            <div className="goals-grid">
              <div className="goal-card">
                <div className="goal-icon-box">
                  <FiTrendingUp className="goal-icon" />
                </div>
                <div className="goal-info">
                  <h4>Expanding Patient Care Services</h4>
                  <p>Continuously improving healthcare services to meet the changing medical needs of our community.</p>
                </div>
              </div>

              <div className="goal-card">
                <div className="goal-icon-box">
                  <FiActivity className="goal-icon" />
                </div>
                <div className="goal-info">
                  <h4>Advanced Surgical Care</h4>
                  <p>Strengthening surgical services with modern operation theatres and evidence-based treatment practices.</p>
                </div>
              </div>

              <div className="goal-card">
                <div className="goal-icon-box">
                  <FiShield className="goal-icon" />
                </div>
                <div className="goal-info">
                  <h4>Safe Healthcare Environment</h4>
                  <p>Maintaining high standards of hygiene, infection prevention and patient safety across the hospital.</p>
                </div>
              </div>

              <div className="goal-card">
                <div className="goal-icon-box">
                  <FiSmile className="goal-icon" />
                </div>
                <div className="goal-info">
                  <h4>To Increase Patient Satisfaction Indices</h4>
                  <p>Achieving outstanding feedback scores through patient-first hospitality and dedicated recovery support.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="goals-graphic-side">
            <div className="goals-visual-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/img8.jpeg`}
                alt="Surgical Excellence"
                className="goals-visual-image"
              />
              <div className="goals-achievement-overlay">
                <FiAward className="achievement-icon" />
                <div>
                  <h5>Continuous Quality Improvement</h5>
                  <p>Committed to annual benchmarks of patient safety and care quality.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
